import { gadgetsWarehouseQueryKey } from './index'

const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.GADGETS)
const categoryGroupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.GADGETS_CATEGORIES)
const partnerGroupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.GADGETS_PARTNERS)

const GADGETS_CATEGORY_FILTER_QUERY_KEY = 'gadgets-category-filter' as const
const gadgetsCategoryFilterQueryKey = { ...useQueryKeyFactory(GADGETS_CATEGORY_FILTER_QUERY_KEY) }

const GADGETS_LOYALTY_LEVEL_FILTER_QUERY_KEY = 'gadgets-loyalty-level-filter' as const
const gadgetsLoyaltyLevelFilterQueryKey = { ...useQueryKeyFactory(GADGETS_LOYALTY_LEVEL_FILTER_QUERY_KEY) }

const GADGETS_PARTNER_FILTER_QUERY_KEY = 'gadgets-partner-filter' as const
const gadgetsPartnerFilterQueryKey = { ...useQueryKeyFactory(GADGETS_PARTNER_FILTER_QUERY_KEY) }

export const apiGadgets = {
	/**
	 * Loyalty Level Filters
	 */
	fetchGadgetsFilterLoyaltyLevel: async (): Promise<IGadgetsLoyaltyLevelFilterModel[]> => {
		const fetchFn = async (): Promise<IGadgetsLoyaltyLevelFilterModel[]> => {
			const { data } = await apiCommon<IGadgetsLoyaltyLevelFilter[]>(`${groupPrefix}/filter-loyalty-level`, {
				method: 'GET',
			})

			return data.map(GadgetsLoyaltyLevelFilter)
		}

		const config = () => ({
			queryKey: gadgetsLoyaltyLevelFilterQueryKey.list(),
			queryFn: async () => await fetchFn(),
		})

		return await useQueryClientService().fetchQuery<IGadgetsLoyaltyLevelFilterModel[]>(config)
	},

	/**
	 * Category Filters
	 */
	fetchGadgetsFilterCategory: async (): Promise<IGadgetsCategoryFilterModel[]> => {
		const fetchFn = async (): Promise<IGadgetsCategoryFilterModel[]> => {
			const { data } = await apiCommon<IGadgetsCategoryFilter[]>(`${groupPrefix}/filter-category`, {
				method: 'GET',
			})

			return data.map(GadgetsCategoryFilter)
		}

		const config = () => ({
			queryKey: gadgetsCategoryFilterQueryKey.list(),
			queryFn: async () => await fetchFn(),
		})

		return await useQueryClientService().fetchQuery<IGadgetsCategoryFilterModel[]>(config)
	},
	fetchGadgetsCategories: async (
		search: IApiSearch = { query: '' },
		pagination: IApiPagination = { page: 1, perPage: 25 },
	): Promise<IApiResponseModel<IGadgetsCategoryModel[]>> => {
		const searchFilter = createSearchQuery(search.query, search.type, search.searchQueryRestriction)

		const { data, meta } = await apiCommon<IGadgetsCategory[]>(`${categoryGroupPrefix}`, {
			method: 'GET',
			...createQuery(pagination, searchFilter),
		})

		return { data: data.map(GadgetsCategoryFilterDetails), meta }
	},
	fetchGadgetsCategoryDetails: async (id: number): Promise<IGadgetsCategoryModel> => {
		const { data } = await apiCommon<IGadgetsCategory>(`${categoryGroupPrefix}/${id}`, {
			method: 'GET',
		})

		return GadgetsCategoryFilterDetails(data)
	},
	createGadgetsCategory: async (payload: IGadgetsCategoryCreatePayload): Promise<IGadgetsCategoryModel> => {
		const { data } = await apiCommon<IGadgetsCategory>(`${categoryGroupPrefix}`, {
			method: 'POST',
			body: payload,
		})

		useQueryClientService().invalidateQueries(gadgetsCategoryFilterQueryKey.lists())

		return GadgetsCategoryFilterDetails(data)
	},
	updateGadgetsCategory: async (payload: IGadgetsCategoryUpdatePayload): Promise<IGadgetsCategoryModel> => {
		const { data } = await apiCommon<IGadgetsCategory>(`${categoryGroupPrefix}/${payload.id}`, {
			method: 'PUT',
			body: payload,
		})

		useQueryClientService().invalidateQueries(gadgetsCategoryFilterQueryKey.lists())

		return GadgetsCategoryFilterDetails(data)
	},
	deleteGadgetsCategory: async (id: number): Promise<void> => {
		await apiCommon<string>(`${categoryGroupPrefix}/${id}`, {
			method: 'DELETE',
		})

		useQueryClientService().invalidateQueries(gadgetsCategoryFilterQueryKey.lists())
	},

	/**
	 * Partner Filters
	 */
	fetchGadgetsFilterPartner: async (): Promise<IGadgetsPartnerFilterModel[]> => {
		const fetchFn = async (): Promise<IGadgetsPartnerFilterModel[]> => {
			const { data } = await apiCommon<IGadgetsPartnerFilter[]>(`${groupPrefix}/filter-partner`, {
				method: 'GET',
			})

			return data.map(GadgetsPartnerFilter)
		}

		const config = () => ({
			queryKey: gadgetsPartnerFilterQueryKey.list(),
			queryFn: async () => await fetchFn(),
		})

		return await useQueryClientService().fetchQuery<IGadgetsPartnerFilterModel[]>(config)
	},
	fetchGadgetsPartners: async (
		search: IApiSearch = { query: '' },
		pagination: IApiPagination = { page: 1, perPage: 25 },
	): Promise<IApiResponseModel<IGadgetsPartnerModel[]>> => {
		const searchFilter = createSearchQuery(search.query, search.type)

		const { data, meta } = await apiCommon<IGadgetsPartner[]>(`${partnerGroupPrefix}`, {
			method: 'GET',
			...createQuery(pagination, searchFilter),
		})

		return { data: data.map(GadgetsPartner), meta }
	},
	fetchGadgetsPartnerDetails: async (id: number): Promise<IGadgetsPartnerModel> => {
		const { data } = await apiCommon<IGadgetsPartner>(`${partnerGroupPrefix}/${id}`, {
			method: 'GET',
		})

		return GadgetsPartner(data)
	},
	createGadgetsPartner: async (payload: IGadgetsPartnerCreatePayload): Promise<IGadgetsPartnerModel> => {
		const { data } = await apiCommon<IGadgetsPartner>(`${partnerGroupPrefix}`, {
			method: 'POST',
			body: payload,
		})

		useQueryClientService().invalidateQueries(gadgetsWarehouseQueryKey.lists())
		useQueryClientService().invalidateQueries(gadgetsPartnerFilterQueryKey.lists())

		return GadgetsPartner(data)
	},
	updateGadgetsPartner: async (payload: IGadgetsPartnerUpdatePayload): Promise<IGadgetsPartnerModel> => {
		const { data } = await apiCommon<IGadgetsPartner>(`${partnerGroupPrefix}/${payload.id}`, {
			method: 'PUT',
			body: payload,
		})

		useQueryClientService().invalidateQueries(gadgetsWarehouseQueryKey.lists())
		useQueryClientService().invalidateQueries(gadgetsPartnerFilterQueryKey.lists())

		return GadgetsPartner(data)
	},
	deleteGadgetsPartner: async (id: number): Promise<void> => {
		await apiCommon<string>(`${partnerGroupPrefix}/${id}`, {
			method: 'DELETE',
		})

		useQueryClientService().invalidateQueries(gadgetsWarehouseQueryKey.lists())
		useQueryClientService().invalidateQueries(gadgetsPartnerFilterQueryKey.lists())
	},
}
